<template>
  <div class="flex flex-col gap-2.5">
    <TrustAccordionItem
      v-for="item in accordionItems"
      :key="item.key"
      :title="$t(item.title)"
      :is-open="accordionState[item.key]"
      :icon="item.icon"
      @toggle-accordion="onAccordionClicked(item)"
    >
      <component
        :is="item.component"
        v-bind="item.props"
      />
    </TrustAccordionItem>
  </div>
</template>

<script lang="ts">
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import TrustAccordionItem from '~/components/molecules/Product/TrustAccordion/TrustAccordionItem.vue';
import ProductHighLights from '~/components/molecules/Product/details/ProductHighlights.vue';
import DeliveryDetails from '~/components/molecules/Product/TrustAccordion/DeliveryDetails.vue';
import PremiumGuarantee from '~/components/molecules/Product/TrustAccordion/PremiumGuarantee.vue';
import SpareParts from '~/components/molecules/Product/TrustAccordion/SpareParts.vue';
import ProductDownloads from '~/components/molecules/Product/details/ProductDownloads/ProductDownloads.vue';
import { ACCORDION_KEYS } from '~~/constants/trustAccordionKeys';
import type { AccordionKey } from '~~/constants/trustAccordionKeys';
import type { AccordionItem } from '~~/types/product/TrustAccordion';
import { isCheckoutDisabled } from '~~/helpers/Checkout/isCheckoutDisabled';
import { scrollToSection } from '~~/helpers/scroll/scrollToSection';

export default defineComponent({
  name: 'TrustAccordion',
  components: {
    TrustAccordionItem,
    ProductHighLights,
    DeliveryDetails,
    PremiumGuarantee,
    SpareParts,
    ProductDownloads,
  },
  props: {
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      required: true,
    },
    manual: {
      type: String,
      required: true,
    },
    shouldShowRelatedAccessories: {
      type: Boolean,
      required: true,
    },
    sku: {
      type: String,
      required: true,
    },
  },
  setup(props) {

    const accordionState = reactive<Record<AccordionKey, boolean>>({
      [ACCORDION_KEYS.KEY_FEATURES]: true,
      [ACCORDION_KEYS.DELIVERY]: false,
      [ACCORDION_KEYS.PREMIUM_GUARANTEE]: false,
      [ACCORDION_KEYS.SPARE_PARTS]: false,
      [ACCORDION_KEYS.DOWNLOADS]: false,
      [ACCORDION_KEYS.ACCESSORIES]: false,
    });

    const { countryCode } = useLocalization();
    const { isExtraGuaranteeEnabled } = useStoreVsfExtended();

    const canShowHighlights = computed(() => !!props.attributesLocalized.highlights?.length);
    const checkoutIsDisabled = isCheckoutDisabled(countryCode.value);
    const accordionItems = computed<AccordionItem[]>(() => [
      ...canShowHighlights.value
        ? [{
          title: 'Key features',
          key: ACCORDION_KEYS.KEY_FEATURES,
          icon: 'iconify svg--trophy',
          component: ProductHighLights,
          props: {
            value: props.attributesLocalized.highlights,
          },
        }]
        : [],
      ...props.shouldShowRelatedAccessories
        ? [{
          title: 'Accessories',
          key: ACCORDION_KEYS.ACCESSORIES,
          icon: 'iconify svg--accessories',
          component: null,
          props: {},
        }]
        : [],
      {
        title: 'Delivery details',
        key: ACCORDION_KEYS.DELIVERY,
        icon: 'iconify svg--truck-empty',
        component: DeliveryDetails,
      },
      ...checkoutIsDisabled
        ? []
        : [{
          title: 'Spare parts',
          key: ACCORDION_KEYS.SPARE_PARTS,
          icon: 'iconify fa-pro-light--bags-shopping',
          component: SpareParts,
          props: {
            sku: props.sku,
          },
        }],
      ...isExtraGuaranteeEnabled.value
        ? [{
          title: 'Premium Guarantee',
          key: ACCORDION_KEYS.PREMIUM_GUARANTEE,
          icon: 'iconify svg--shield',
          component: PremiumGuarantee,
        }]
        : [],
      {
        title: 'User Manual',
        key: ACCORDION_KEYS.DOWNLOADS,
        icon: 'iconify svg--download-file text-xl',
        component: ProductDownloads,
        props: {
          manual: props.manual,
          sku: props.sku,
        },
      },
    ]);

    const onAccordionClicked = (item: AccordionItem): void => {

      if (item.key === ACCORDION_KEYS.ACCESSORIES) {

        if (props.shouldShowRelatedAccessories) {

          scrollToSection('#product-accessories');

        }

      } else {

        accordionState[item.key] = !accordionState[item.key];

      }

    };

    return {
      accordionState,
      onAccordionClicked,
      accordionItems,
    };

  },
});
</script>
